<template>
  <div class="container">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>Dashboard de Circuitos de Estudios</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="4">
            <v-select
              v-model="circuitoSeleccionado"
              :items="circuitos"
              item-text="nombre"
              item-value="id"
              label="Seleccionar Circuito"
              dense
              outlined
              prepend-icon="mdi-clipboard-list"
              @change="cargarElegibles"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="estadosFiltro"
              :items="opcionesEstado"
              item-text="text"
              item-value="value"
              label="Filtrar por Estado"
              dense
              outlined
              multiple
              chips
              small-chips
              deletable-chips
              prepend-icon="mdi-filter"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filtroPaciente"
              label="Buscar paciente"
              prepend-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="circuitoSeleccionado && circuitoActual && circuitoActual.notificarPaciente" class="mb-2">
          <v-col cols="12">
            <v-btn
              color="blue"
              dark
              :disabled="pacientesSeleccionados.length === 0"
              :loading="enviandoNotificacion"
              @click="confirmarNotificacion"
            >
              <v-icon left>mdi-bell</v-icon>
              Notificar ({{ pacientesSeleccionados.length }})
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          v-if="circuitoSeleccionado"
          v-model="pacientesSeleccionados"
          :headers="headers"
          :items="pacientesFiltrados"
          :show-select="circuitoActual && circuitoActual.notificarPaciente"
          class="elevation-1"
          locale="es-ar"
          item-key="idPaciente"
          :disable-sort="false"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageText: 'Filas por página',
          }"
        >
          <template v-slot:item.data-table-select="{ item, isSelected, select }">
            <v-simple-checkbox
              :value="isSelected"
              :disabled="!esNotificable(item)"
              @input="select($event)"
              :ripple="false"
            ></v-simple-checkbox>
          </template>

          <template v-slot:item.fechaProximaRealizacion="{ item }">
            {{ formatFecha(item.fechaProximaRealizacion) }}
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip
              small
              :color="colorEstado(item.estado)"
              dark
            >
              {{ formatEstado(item.estado) }}
            </v-chip>
          </template>

          <template v-slot:item.acciones="{ item }">
            <div :key="item.idPaciente + '-' + item.estado" style="display: flex; align-items: center; gap: 4px; justify-content: flex-end;">
              <v-tooltip top v-if="item.estado === 'PENDIENTE' || item.estado === 'NOTIFICADO' || item.estado === 'PENDIENTE_RENOVACION'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="green"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmarManualDialog(item)"
                  >mdi-check-circle</v-icon>
                </template>
                <span>Confirmar manualmente</span>
              </v-tooltip>
              <v-tooltip top v-if="item.estado === 'CONFIRMADO' || item.estado === 'EN_CURSO'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="purple"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-2"
                    @click="abrirAsignacion(item)"
                  >mdi-calendar-clock</v-icon>
                </template>
                <span>Asignar Turnos</span>
              </v-tooltip>
              <v-tooltip top v-if="item.idCircuitoPaciente">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="red"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-2"
                    @click="confirmarEliminar(item)"
                >mdi-delete</v-icon>
              </template>
              <span>Eliminar del circuito</span>
            </v-tooltip>
            </div>
          </template>
        </v-data-table>

        <!-- Dialog de asignación de turnos (componente implementado en tarea 11) -->
        <AsignarTurnosCircuito
          v-if="dialogAsignar"
          :visible="dialogAsignar"
          :paciente="pacienteAsignar"
          :idCircuito="circuitoSeleccionado"
          @cerrar="cerrarAsignacion"
        />

        <v-alert class="mt-4" color="purple" dark outlined v-if="!circuitoSeleccionado">
          Seleccione un circuito para ver los pacientes elegibles.
        </v-alert>

        <v-alert class="mt-4" color="info" dark outlined v-else-if="pacientesFiltrados.length === 0">
          No hay pacientes elegibles para el circuito seleccionado con los filtros aplicados.
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from "../../axios-auth.js";
import AsignarTurnosCircuito from "./AsignarTurnosCircuito.vue";

export default {
  components: {
    AsignarTurnosCircuito,
  },
  data() {
    return {
      circuitos: [],
      circuitoSeleccionado: null,
      pacientesElegibles: [],
      pacientesSeleccionados: [],
      estadosFiltro: [],
      filtroPaciente: "",
      enviandoNotificacion: false,
      dialogAsignar: false,
      pacienteAsignar: null,
      opcionesEstado: [
        { text: "PENDIENTE", value: "PENDIENTE" },
        { text: "PENDIENTE RENOVACION", value: "PENDIENTE_RENOVACION" },
        { text: "NOTIFICADO", value: "NOTIFICADO" },
        { text: "CONFIRMADO", value: "CONFIRMADO" },
        { text: "EN CURSO", value: "EN_CURSO" },
        { text: "COMPLETADO", value: "COMPLETADO" },
      ],
      headers: [
        { text: "Paciente", value: "nombrePaciente" },
        { text: "DNI", value: "dni" },
        { text: "Fecha Próxima", value: "fechaProximaRealizacion" },
        { text: "Estado", value: "estado" },
        { text: "Estudios Pendientes", value: "estudiosPendientes" },
        { text: "Acciones", value: "acciones", sortable: false },
      ],
    };
  },
  computed: {
    circuitoActual() {
      if (!this.circuitoSeleccionado) return null;
      return this.circuitos.find((c) => c.id === this.circuitoSeleccionado) || null;
    },
    pacientesFiltrados() {
      let resultado = this.pacientesElegibles;

      if (this.estadosFiltro.length > 0) {
        resultado = resultado.filter(
          (p) => this.estadosFiltro.includes(p.estado)
        );
      }

      if (this.filtroPaciente && this.filtroPaciente.trim() !== "") {
        const busqueda = this.filtroPaciente.trim().toLowerCase();
        resultado = resultado.filter(
          (p) => p.nombrePaciente && p.nombrePaciente.toLowerCase().includes(busqueda)
        );
      }

      resultado = [...resultado].sort((a, b) => {
        return (a.nombrePaciente || "").localeCompare(b.nombrePaciente || "");
      });

      return resultado;
    },
  },
  methods: {
    cargarCircuitos() {
      let self = this;
      axios
        .get("/circuitos")
        .then((response) => {
          if (response.data != null && response.data != "")
            self.circuitos = response.data.filter((c) => c.activo);
          else self.circuitos = [];
        })
        .catch((error) => {
          self.showAlert({
            icon: "error",
            title: "Error al cargar los circuitos",
            vm: self,
          });
        });
    },
    cargarElegibles() {
      this.pacientesSeleccionados = [];
      if (!this.circuitoSeleccionado) {
        this.pacientesElegibles = [];
        return;
      }
      let self = this;
      axios
        .get("/circuitos/" + this.circuitoSeleccionado + "/elegibles")
        .then((response) => {
          if (response.data != null && response.data != "")
            self.pacientesElegibles = response.data;
          else self.pacientesElegibles = [];
        })
        .catch((error) => {
          self.showAlert({
            icon: "error",
            title: "Error al cargar pacientes elegibles",
            vm: self,
          });
        });
    },
    confirmarNotificacion() {
      let self = this;
      this.$swal.fire({
        title: "Notificar Pacientes",
        text: `¿Desea enviar notificación a ${this.pacientesSeleccionados.length} paciente(s)?`,
        icon: "question",
        background: "#ececec",
        showCancelButton: true,
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          self.notificarPacientes();
        }
      });
    },
    notificarPacientes() {
      let self = this;
      self.enviandoNotificacion = true;
      const idPacientes = self.pacientesSeleccionados.map((p) => p.idPaciente);
      axios
        .post("/circuitos/" + self.circuitoSeleccionado + "/notificar", idPacientes)
        .then((response) => {
          self.enviandoNotificacion = false;
          self.pacientesSeleccionados = [];
          self.showAlert({
            icon: "success",
            title: "Notificaciones enviadas exitosamente",
            vm: self,
          });
          self.cargarElegibles();
        })
        .catch((error) => {
          self.enviandoNotificacion = false;
          self.showAlert({
            icon: "error",
            title: "Error al enviar las notificaciones",
            vm: self,
          });
        });
    },
    confirmarManualDialog(item) {
      let self = this;
      this.$swal.fire({
        title: "Confirmar Paciente",
        text: `¿Desea confirmar manualmente a ${item.nombrePaciente}?`,
        icon: "question",
        background: "#ececec",
        showCancelButton: true,
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          self.confirmarManual(item);
        }
      });
    },
    abrirAsignacion(item) {
      this.pacienteAsignar = item;
      this.dialogAsignar = true;
    },
    cerrarAsignacion() {
      this.dialogAsignar = false;
      this.pacienteAsignar = null;
      this.cargarElegibles();
    },
    confirmarManual(item) {
      let self = this;
      const payload = {
        idCircuitoPaciente: item.idCircuitoPaciente || null,
        idPaciente: item.idPaciente,
        idCircuito: this.circuitoSeleccionado,
      };
      axios
        .post("/circuitos/confirmar-manual", payload)
        .then((response) => {
          self.showAlert({
            icon: "success",
            title: "Paciente confirmado exitosamente",
            vm: self,
          });
          self.cargarElegibles();
        })
        .catch((error) => {
          self.showAlert({
            icon: "error",
            title: "Error al confirmar el paciente",
            vm: self,
          });
        });
    },
    confirmarEliminar(item) {
      let self = this;
      this.$swal.fire({
        title: "Eliminar del Circuito",
        text: `¿Desea eliminar a ${item.nombrePaciente} del circuito? Se cancelarán los turnos programados asociados.`,
        icon: "warning",
        background: "#ececec",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Si, eliminar",
        cancelButtonText: "No",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          self.eliminarPaciente(item);
        }
      });
    },
    eliminarPaciente(item) {
      let self = this;
      axios
        .delete("/circuitos/paciente/" + item.idCircuitoPaciente)
        .then((response) => {
          self.showAlert({
            icon: "success",
            title: "Paciente eliminado del circuito exitosamente",
            vm: self,
          });
          self.cargarElegibles();
        })
        .catch((error) => {
          self.showAlert({
            icon: "error",
            title: "Error al eliminar el paciente del circuito",
            vm: self,
          });
        });
    },
    formatFecha(fecha) {
      if (!fecha) return "-";
      const date = new Date(fecha);
      return date.toLocaleDateString("es-AR");
    },
    formatEstado(estado) {
      if (!estado) return "";
      return estado.replace(/_/g, " ");
    },
    esNotificable(item) {
      return item.estado === "PENDIENTE" || item.estado === "PENDIENTE_RENOVACION";
    },
    colorEstado(estado) {
      switch (estado) {
        case "PENDIENTE":
          return "orange";
        case "PENDIENTE_RENOVACION":
          return "deep-orange";
        case "NOTIFICADO":
          return "blue";
        case "CONFIRMADO":
          return "green";
        case "EN_CURSO":
          return "purple";
        case "COMPLETADO":
          return "teal";
        default:
          return "grey";
      }
    },
    showAlert(alertInfo) {
      alertInfo.vm.$swal.fire({
        icon: alertInfo.icon,
        title: alertInfo.title,
        showConfirmButton: false,
        background: "#ececec",
        timer: 2500,
      });
    },
  },
  created() {
    this.cargarCircuitos();
  },
};
</script>
